import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
} from 'react'
import { Helmet } from 'react-helmet'
import { useParams, Link, useLocation } from 'react-router-dom'
import {
  Dropdown,
  Spinner,
  Modal,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import classNames from 'classnames'
import {
  FeatherLoader,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import { SortableHeaderCell } from '../table'
import Axios from 'axios'
import AddUserModal from './AddUserModal'
import UserDetailsModal from './UserDetailsModal'
import ManualPaymentModal from './ManualPaymentModal'
import ExperienceConfirmModal from './ExperienceConfirmModal'
import QuestTypeformUserResponse from './QuestTypeformUserResponse'
import API from '../../utils/API'
import { UTCFormatDate } from '../../utils/date'
import {
  clearTravelDocumentPreviewUrlCache,
  getCachedTravelDocumentPresignedUrl,
  invalidateCachedTravelDocumentPresignedUrl,
  isPresignedDocumentUrlFresh,
  isTravelDocumentImageFileName,
  isTravelDocumentPdfFileName,
  setCachedTravelDocumentPresignedUrl,
} from '../../utils/travelDocumentPreviewUrlCache'

// eslint-disable-next-line react/display-name
const CustomToggle = React.forwardRef(({ onClick }, ref) => (
  <button
    type='button'
    className='experience-muted-btn'
    ref={ref}
    onClick={(e) => {
      e.preventDefault()
      onClick(e)
    }}>
    •••
  </button>
))

const PARTICIPANT_SORT_BY = {
  NAME: 'name',
  DATE: 'date',
}

const DOCUMENT_TYPES_FOR_STATS = ['ticket', 'insurance', 'passport', 'headshot']

const getParticipantName = (participant) =>
  (participant?.userId?.fullName || '').trim().toLowerCase()

const getParticipantPaymentProgress = (participant) => {
  const totalPaid = Number(participant?.totalPaid) || 0
  const totalAmount = Number(participant?.totalAmount) || 0
  const pct =
    totalAmount > 0
      ? Math.min(100, Math.round((totalPaid / totalAmount) * 100))
      : totalPaid > 0
      ? 100
      : 0
  const label =
    totalPaid === 0
      ? 'Nothing paid'
      : `$${totalPaid.toLocaleString()} of $${totalAmount.toLocaleString()}`
  return {
    label,
    pct,
    isPartial: pct < 100,
    outstanding: Math.max(0, totalAmount - totalPaid),
  }
}

const getParticipantEnrollTime = (participant) =>
  new Date(participant?.createdAt || 0).getTime()

const participantHasDocumentType = (participant, documentType) => {
  const travelDocuments = participant?.travelDocuments || []
  return travelDocuments.some(
    (d) =>
      d?.documentType === documentType &&
      Array.isArray(d?.fileName) &&
      d.fileName.some(
        (name) => typeof name === 'string' && name.trim().length > 0,
      ),
  )
}
const QuestsParticipants = ({
  questDetail,
  onHeaderActionsChange,
  onManageDataChange,
}) => {
  const [showModal, setShowModal] = useState(false)
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false)
  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [investments, setInvestments] = useState(null)
  const [selectedParticipant, setSelectedParticipant] = useState(null)
  const [participantToRemove, setParticipantToRemove] = useState(null)
  const [isRemoving, setIsRemoving] = useState(false)
  const [questParticipants, setQuestParticipants] = useState([])
  const [participantsSummary, setParticipantsSummary] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [customFormId, setCustomFormId] = useState(null)
  const [exportLoading, setExportLoading] = useState(false)
  const [downloadDocsLoading, setDownloadDocsLoading] = useState(false)
  const [selectedEnrollmentUser, setSelectedEnrollmentUser] = useState(null)
  const [docDownloadLoading, setDocDownloadLoading] = useState({})
  const [docViewLoading, setDocViewLoading] = useState({})
  const [documentPreview, setDocumentPreview] = useState({
    show: false,
    files: [],
    activeIndex: 0,
    title: '',
    userName: '',
    userId: '',
    previewSession: 0,
  })
  const documentPreviewRef = useRef(documentPreview)
  documentPreviewRef.current = documentPreview
  const [actionLoading, setActionLoading] = useState({
    type: null,
    userId: null,
  })
  const [showHighlightedPart, setShowHighlightedPart] = useState(false)
  const [previewMediaReady, setPreviewMediaReady] = useState(false)
  const [participantSortBy, setParticipantSortBy] = useState(
    PARTICIPANT_SORT_BY.NAME,
  )
  const [participantSortAsc, setParticipantSortAsc] = useState(true)
  const { questId } = useParams()
  const location = useLocation()
  const timeIntervalRef = useRef(null)
  const Error = useErrorService()
  const Notification = useNotificationService()

  useEffect(() => {
    return () => {
      const prev = documentPreviewRef.current
      ;(prev.files || []).forEach((f) => {
        if (f.blobUrl) {
          try {
            URL.revokeObjectURL(f.blobUrl)
          } catch {
            // ignore
          }
        }
      })
    }
  }, [])

  const hasAnyEnrollmentFormSubmitted = Array.isArray(questParticipants)
    ? questParticipants.some((p) => !!p?.customFormSubmittedAt)
    : false

  const hasAnyTravelDocuments = Array.isArray(questParticipants)
    ? questParticipants.some(
        (p) =>
          Array.isArray(p?.travelDocuments) && p.travelDocuments.length > 0,
      )
    : false

  const sortedQuestParticipants = useMemo(() => {
    const participants = [...questParticipants]
    const direction = participantSortAsc ? 1 : -1

    participants.sort((a, b) => {
      if (participantSortBy === PARTICIPANT_SORT_BY.NAME) {
        const nameCompare = getParticipantName(a).localeCompare(
          getParticipantName(b),
        )
        if (nameCompare !== 0) {
          return nameCompare * direction
        }
        return (
          (getParticipantEnrollTime(a) - getParticipantEnrollTime(b)) *
          direction
        )
      }

      const dateCompare =
        getParticipantEnrollTime(a) - getParticipantEnrollTime(b)
      if (dateCompare !== 0) {
        return dateCompare * direction
      }
      return (
        getParticipantName(a).localeCompare(getParticipantName(b)) * direction
      )
    })

    return participants
  }, [questParticipants, participantSortBy, participantSortAsc])

  const participantStats = useMemo(() => {
    const summary = participantsSummary
    if (summary && typeof summary === 'object') {
      return {
        rosterCount:
          summary.enrolledCount ??
          summary.totalParticipants ??
          questParticipants.length,
        rosterTotal: summary.totalParticipants ?? null,
        outstandingTotal: Number(summary.outstandingAmount) || 0,
        outstandingPeople: Number(summary.outstandingPeopleCount) || 0,
        docsMissing: Number(summary.documentsMissingCount) || 0,
        docsMissingPeople: Number(summary.documentsMissingPeopleCount) || 0,
        fromApi: true,
      }
    }

    let outstandingTotal = 0
    let outstandingPeople = 0
    let docsMissing = 0
    let docsMissingPeople = 0

    questParticipants.forEach((participant) => {
      const { outstanding } = getParticipantPaymentProgress(participant)
      if (outstanding > 0) {
        outstandingTotal += outstanding
        outstandingPeople += 1
      }

      let personMissing = 0
      DOCUMENT_TYPES_FOR_STATS.forEach((documentType) => {
        if (!participantHasDocumentType(participant, documentType)) {
          docsMissing += 1
          personMissing += 1
        }
      })
      if (personMissing > 0) {
        docsMissingPeople += 1
      }
    })

    return {
      rosterCount: questParticipants.length,
      rosterTotal: null,
      outstandingTotal,
      outstandingPeople,
      docsMissing,
      docsMissingPeople,
      fromApi: false,
    }
  }, [questParticipants, participantsSummary])

  const rosterCapacity =
    participantStats.rosterTotal ??
    questDetail?.capacity ??
    questDetail?.maxParticipants ??
    null
  const handleParticipantSortByName = () => {
    if (participantSortBy === PARTICIPANT_SORT_BY.NAME) {
      setParticipantSortAsc((prev) => !prev)
      return
    }
    setParticipantSortBy(PARTICIPANT_SORT_BY.NAME)
    setParticipantSortAsc(true)
  }

  const handleParticipantSortByDate = () => {
    if (participantSortBy === PARTICIPANT_SORT_BY.DATE) {
      setParticipantSortAsc((prev) => !prev)
      return
    }
    setParticipantSortBy(PARTICIPANT_SORT_BY.DATE)
    setParticipantSortAsc(true)
  }

  const documentTypesOrder = ['ticket', 'insurance', 'passport', 'headshot']
  const getDocumentLoadingKey = (userId, documentType) =>
    `${userId || 'user'}-${documentType || 'document'}`

  const getParticipants = async ({ refreshRail = false } = {}) => {
    setIsLoading(true)
    try {
      const { data } = await API.AdminAPI.quests.getParticipants(questId)
      // Handle API response shape: { participants, summary, customForm, isPublished }
      const participants = Array.isArray(data?.participants)
        ? data.participants
        : Array.isArray(data)
        ? data
        : []
      setQuestParticipants(participants)
      setParticipantsSummary(
        data?.summary && typeof data.summary === 'object' ? data.summary : null,
      )
      setCustomFormId(data?.customForm?._id || null)
      setIsLoading(false)
      if (refreshRail && typeof onManageDataChange === 'function') {
        onManageDataChange()
      }
    } catch (e) {
      Error.showError(e)
      setIsLoading(false)
    }
  }
  const refreshParticipants = () => getParticipants({ refreshRail: true })
  const deleteQuestUser = async (userId) => {
    setIsRemoving(true)
    try {
      await API.AdminAPI.quests.deleteQuestUser(questId, userId)
      Notification.showNotification(
        'The user has been removed from the list.',
        'success',
      )
      setParticipantToRemove(null)
      refreshParticipants()
    } catch (e) {
      Error.showError(e)
    } finally {
      setIsRemoving(false)
    }
  }
  useEffect(() => {
    getParticipants()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId])

  useEffect(() => {
    return () => {
      clearTravelDocumentPreviewUrlCache()
    }
  }, [questId, location.pathname])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await API.AdminAPI.quests.getQuestDetail(questId)
        setInvestments(data.investments)
      } catch (err) {
        Error.showError(err)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const rawHash = location?.hash || ''
    const hashUserId = rawHash.replace(/^#/, '').trim()

    if (!hashUserId) {
      setShowHighlightedPart(false)
      return () => {
        if (timeIntervalRef.current) {
          clearInterval(timeIntervalRef.current)
          timeIntervalRef.current = null
        }
      }
    }

    if (!questParticipants?.length) {
      return () => {
        if (timeIntervalRef.current) {
          clearInterval(timeIntervalRef.current)
          timeIntervalRef.current = null
        }
      }
    }

    const participantExists = questParticipants.some(
      (p) => String(p?.userId?._id || '') === hashUserId,
    )

    if (participantExists) {
      setShowHighlightedPart(true)
      requestAnimationFrame(() => {
        const el = document.getElementById(hashUserId)
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        }
      })
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current)
      }
      timeIntervalRef.current = setInterval(() => {
        setShowHighlightedPart(false)
        if (timeIntervalRef.current) {
          clearInterval(timeIntervalRef.current)
          timeIntervalRef.current = null
        }
      }, 3000)
    } else {
      setShowHighlightedPart(false)
    }

    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current)
        timeIntervalRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.hash, questParticipants])

  const handleAddNewQuestClick = () => {
    setShowModal(true)
  }

  const handleExportReport = async () => {
    if (!questId || !customFormId) return

    let xlsxFile, downloadLink, filename
    setExportLoading(true)

    try {
      filename = `${questDetail?.title} (Experience) - Answer Report.xlsx`

      const { data } = await Axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/quests/${questId}/custom-form/${customFormId}/answers/report`,
        { responseType: 'blob', withCredentials: true },
      )

      if (data) {
        xlsxFile = new Blob([data])
        downloadLink = document.createElement('a')
        downloadLink.download = filename
        downloadLink.href = window.URL.createObjectURL(xlsxFile)
        downloadLink.style.display = 'none'
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
        window.URL.revokeObjectURL(downloadLink.href)

        Notification.showNotification(
          'Report downloaded successfully.',
          'success',
        )
      }
    } catch (e) {
      Error.showError(e)
    } finally {
      setExportLoading(false)
    }
  }

  const handleDownloadTravelDocuments = () => {
    if (!questId) return

    setDownloadDocsLoading(true)

    try {
      const url = `${process.env.REACT_APP_API_ENDPOINT}/quests/${questId}/travel-documents/zip-download`

      // Trigger direct download (best approach)
      window.location.href = url

      Notification.showNotification(
        'Download started. Your file will be saved shortly.',
        'success',
      )
    } catch (e) {
      Error.showError(e)
    } finally {
      setDownloadDocsLoading(false)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const handleCloseUserDetailsModal = () => {
    setShowUserDetailsModal(false)
    setSelectedUser(null)
    setSelectedParticipant(null)
  }

  const handleCloseManualPaymentModal = () => {
    setShowManualPaymentModal(false)
    setSelectedParticipant(null)
  }

  const handleUpdateClick = async (participant) => {
    try {
      const { data } = await API.AdminAPI.quests.getSingleParticipant(
        questId,
        participant.userId._id,
      )
      setSelectedUser(data)
      setSelectedParticipant(data)
      setShowUserDetailsModal(true)
    } catch (e) {
      Error.showError(e)
    }
  }

  const handleManualPayment = (participant) => {
    setSelectedParticipant(participant)
    setShowManualPaymentModal(true)
  }

  const isInviteActionLoading = (userId, type) =>
    actionLoading.userId === userId && actionLoading.type === type

  const isAnyInviteActionLoading = (userId) =>
    actionLoading.userId === userId &&
    (actionLoading.type === 'resend' || actionLoading.type === 'copy')

  const copyTextToClipboard = async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        return true
      }

      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.setAttribute('readonly', '')
      textArea.style.position = 'absolute'
      textArea.style.left = '-9999px'
      document.body.appendChild(textArea)
      textArea.select()
      const isSuccess = document.execCommand('copy')
      document.body.removeChild(textArea)
      return isSuccess
    } catch (err) {
      return false
    }
  }

  const handleResendInvitationEmail = async (participant) => {
    const userId = participant?.userId?._id
    if (!userId) {
      Error.showError({
        message: 'User information missing for this participant.',
      })
      return
    }

    setActionLoading({ type: 'resend', userId })

    try {
      await API.AdminAPI.quests.resendParticipantInvite(questId, userId)
      Notification.showNotification(
        'Invitation email has been resent successfully.',
        'success',
      )
    } catch (e) {
      Error.showError(e)
    } finally {
      setActionLoading({ type: null, userId: null })
    }
  }

  const handleCopyInvitationLink = async (participant) => {
    const userId = participant?.userId?._id
    if (!userId) {
      Error.showError({
        message: 'User information missing for this participant.',
      })
      return
    }

    setActionLoading({ type: 'copy', userId })

    try {
      const { data } = await API.AdminAPI.quests.generateParticipantInviteLink(
        questId,
        userId,
      )

      const invitationLink = data?.invitationLink || ''

      const formattedLink =
        typeof invitationLink === 'string' ? invitationLink.trim() : ''

      if (!formattedLink) {
        Notification.showNotification(
          'Invitation link generated successfully.',
          'danger',
        )
        return
      }

      const copied = await copyTextToClipboard(formattedLink)

      if (copied) {
        Notification.showNotification(
          'Invitation link copied successfully.',
          'success',
        )
      } else {
        Notification.showNotification(
          `Invitation link generated. Unable to copy automatically. ${formattedLink}`,
          'danger',
        )
      }
    } catch (e) {
      Error.showError(e)
    } finally {
      setActionLoading({ type: null, userId: null })
    }
  }

  const getTravelDocumentFileNames = (participant, documentType) => {
    const travelDocuments = participant?.travelDocuments || []
    const fileNames = travelDocuments
      .filter((d) => d?.documentType === documentType)
      .flatMap((d) => (Array.isArray(d?.fileName) ? d.fileName : []))
    return fileNames.filter(
      (name) => typeof name === 'string' && name.trim().length > 0,
    )
  }

  const getTravelDocumentFileName = (participant, documentType) => {
    const fileNames = getTravelDocumentFileNames(participant, documentType)
    return fileNames.length > 0 ? fileNames[0] : null
  }

  const getDocumentLabel = (documentType) => {
    switch (documentType) {
      case 'ticket':
        return 'Air Ticket'
      case 'insurance':
        return 'Insurance'
      case 'passport':
        return 'Passport'
      case 'headshot':
        return 'Headshot'
      default:
        return documentType
    }
  }

  const sanitizeFileNamePart = (value) => {
    if (!value || typeof value !== 'string') return ''
    return value
      .trim()
      .replace(/[\\/:*?"<>|]+/g, '-') // invalid on Windows / generally safe
      .replace(/\s+/g, ' ')
  }

  const getFileExtensionFromUrlOrName = (urlOrName) => {
    if (!urlOrName || typeof urlOrName !== 'string') return ''
    const clean = urlOrName.split('?')[0]
    const lastDot = clean.lastIndexOf('.')
    if (lastDot === -1) return ''
    const ext = clean.slice(lastDot + 1).toLowerCase()
    return ext ? `.${ext}` : ''
  }

  const triggerBlobDownload = (blob, filename) => {
    const downloadLink = document.createElement('a')
    downloadLink.download = filename
    downloadLink.href = window.URL.createObjectURL(blob)
    downloadLink.style.display = 'none'
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
    window.URL.revokeObjectURL(downloadLink.href)
  }

  const getDownloadDocumentUrl = (participant, documentType) => {
    const userId = participant?.userId?._id
    if (!questId || !userId) return ''

    return `${process.env.REACT_APP_API_ENDPOINT}/quests/${questId}/users/${userId}/documents/download?documentType=${documentType}`
  }

  const getTravelDocumentDownloadResponse = async (
    participant,
    documentType,
  ) => {
    const userId = participant?.userId?._id
    if (!questId || !userId) return null

    const downloadUrl = getDownloadDocumentUrl(participant, documentType)
    const res = await Axios.get(downloadUrl, {
      responseType: 'arraybuffer',
      withCredentials: true,
    })

    const contentType = res?.headers?.['content-type'] || ''
    if (contentType.includes('application/json')) {
      const jsonText = new TextDecoder().decode(res.data)
      const payload = JSON.parse(jsonText || '{}')
      return {
        isSingleDocument: !!payload?.singleDocument && !!payload?.url,
        url: payload?.url || '',
      }
    }

    return {
      isSingleDocument: false,
      blob: new Blob([res.data], { type: contentType || 'application/zip' }),
    }
  }

  const handleDownloadTravelDocument = async (participant, documentType) => {
    const userId = participant?.userId?._id
    if (!questId || !userId) return
    const loadingKey = getDocumentLoadingKey(userId, documentType)

    setDocDownloadLoading((prev) => ({ ...prev, [loadingKey]: true }))

    const questName = sanitizeFileNamePart(questDetail?.title || 'Quest')
    const userName = sanitizeFileNamePart(
      participant?.userId?.fullName || 'User',
    )
    const columnName = sanitizeFileNamePart(getDocumentLabel(documentType))

    try {
      const matchingDocuments = (participant?.travelDocuments || []).filter(
        (doc) => doc?.documentType === documentType,
      )
      const fileNames = getTravelDocumentFileNames(participant, documentType)
      if (!fileNames.length) {
        Error.showError({ message: 'No document available to download.' })
        return
      }

      const isZipDownload = matchingDocuments.length > 1 || fileNames.length > 1

      const response = await getTravelDocumentDownloadResponse(
        participant,
        documentType,
      )

      if (!response) return

      if (response.isSingleDocument) {
        const extFromExisting = getFileExtensionFromUrlOrName(
          getTravelDocumentFileName(participant, documentType),
        )
        const extFromUrl = getFileExtensionFromUrlOrName(response.url)
        const extension = extFromExisting || extFromUrl || ''

        const fileName = `${questName} - ${userName} - ${columnName}${extension}`

        const fileRes = await Axios.get(response.url, { responseType: 'blob' })
        triggerBlobDownload(fileRes.data, fileName)
        return
      }

      if (response.blob) {
        const zipName = `${questName} - ${userName} - ${columnName}.zip`
        triggerBlobDownload(response.blob, zipName)
        if (isZipDownload) {
          Notification.showNotification(
            'Download started. Your file will be saved shortly.',
            'success',
          )
        }
        return
      }

      Error.showError({
        message: 'Unable to download travel documents.',
      })
    } catch (e) {
      Error.showError(e)
    } finally {
      setDocDownloadLoading((prev) => {
        const updated = { ...prev }
        delete updated[loadingKey]
        return updated
      })
    }
  }

  const handleViewTravelDocument = async (participant, documentType) => {
    const userId = participant?.userId?._id
    if (!questId || !userId) return
    const loadingKey = getDocumentLoadingKey(userId, documentType)

    setDocViewLoading((prev) => ({ ...prev, [loadingKey]: true }))

    try {
      const allUserFiles = documentTypesOrder.flatMap((type) => {
        const files = getTravelDocumentFileNames(participant, type)
        return files.map((fileName) => ({
          fileName,
          documentType: type,
          label: getDocumentLabel(type),
        }))
      })

      if (!allUserFiles.length) {
        Notification.showNotification(
          'No document available to preview.',
          'danger',
        )
        return
      }

      const previewFiles = allUserFiles.map((file) => ({
        fileName: file.fileName,
        label: file.label,
        documentType: file.documentType,
        url: '',
        blobUrl: '',
        isLoading: true,
        loadError: false,
      }))

      const startIndex = previewFiles.findIndex(
        (file) => file.documentType === documentType,
      )
      const activeIndex = startIndex >= 0 ? startIndex : 0

      setDocumentPreview({
        show: true,
        files: previewFiles,
        activeIndex,
        title: getDocumentLabel(documentType),
        userName: participant?.userId?.fullName || 'User',
        userId,
        previewSession: Date.now(),
      })
    } catch (e) {
      Error.showError(e)
    } finally {
      setDocViewLoading((prev) => {
        const updated = { ...prev }
        delete updated[loadingKey]
        return updated
      })
    }
  }

  const goToPreviousDocument = () => {
    setDocumentPreview((prev) => {
      if (!prev.files?.length) return prev
      const nextIndex = Math.max(prev.activeIndex - 1, 0)
      return { ...prev, activeIndex: nextIndex }
    })
  }

  const goToNextDocument = () => {
    setDocumentPreview((prev) => {
      if (!prev.files?.length) return prev
      const nextIndex = Math.min(prev.activeIndex + 1, prev.files.length - 1)
      return { ...prev, activeIndex: nextIndex }
    })
  }

  const closeDocumentPreview = () => {
    setDocumentPreview((prev) => {
      ;(prev.files || []).forEach((f) => {
        if (f.blobUrl) {
          try {
            URL.revokeObjectURL(f.blobUrl)
          } catch {
            // ignore
          }
        }
      })
      return {
        show: false,
        files: [],
        activeIndex: 0,
        title: '',
        userName: '',
        userId: '',
        previewSession: 0,
      }
    })
    setPreviewMediaReady(false)
  }

  useEffect(() => {
    if (!documentPreview.show || !questId || !documentPreview.userId) {
      return undefined
    }

    const session = documentPreview.previewSession
    const userId = documentPreview.userId
    const activeIndexAtStart = documentPreview.activeIndex
    let cancelled = false

    const patchFile = (index, partial) => {
      if (cancelled) return
      setDocumentPreview((prev) => {
        if (
          !prev.show ||
          prev.previewSession !== session ||
          prev.userId !== userId
        ) {
          return prev
        }
        const nextFiles = [...(prev.files || [])]
        if (!nextFiles[index]) return prev
        nextFiles[index] = { ...nextFiles[index], ...partial }
        return { ...prev, files: nextFiles }
      })
    }

    const resolvePresigned = async (fileName, allowNetwork) => {
      let url = getCachedTravelDocumentPresignedUrl(questId, userId, fileName)
      if (url) return url
      if (!allowNetwork) return ''
      try {
        const { data } = await API.ClientAPI.quests.downloadTravelDocument(
          questId,
          userId,
          fileName,
        )
        const next = data?.url || ''
        if (next) {
          setCachedTravelDocumentPresignedUrl(questId, userId, fileName, next)
        }
        return next
      } catch (err) {
        Error.showError(err)
        return ''
      }
    }

    const loadPdfBlobForIndex = async (index, fileName, presignedHint) => {
      const applyBlob = (presignedUrl, blob) => {
        const blobUrl = URL.createObjectURL(blob)
        setDocumentPreview((prev) => {
          if (
            !prev.show ||
            prev.previewSession !== session ||
            prev.userId !== userId
          ) {
            URL.revokeObjectURL(blobUrl)
            return prev
          }
          const nextFiles = [...(prev.files || [])]
          if (!nextFiles[index]) {
            URL.revokeObjectURL(blobUrl)
            return prev
          }
          const old = nextFiles[index].blobUrl
          if (old) {
            try {
              URL.revokeObjectURL(old)
            } catch {
              // ignore
            }
          }
          nextFiles[index] = {
            ...nextFiles[index],
            url: presignedUrl,
            blobUrl,
            isLoading: false,
            loadError: false,
          }
          return { ...prev, files: nextFiles }
        })
      }

      let presigned =
        presignedHint && isPresignedDocumentUrlFresh(presignedHint)
          ? presignedHint
          : await resolvePresigned(fileName, true)

      if (cancelled || !presigned) {
        patchFile(index, {
          isLoading: false,
          loadError: true,
          blobUrl: '',
        })
        return
      }

      try {
        const res = await Axios.get(presigned, { responseType: 'blob' })
        if (cancelled) return
        applyBlob(presigned, res.data)
      } catch (e) {
        const status = e?.response?.status
        if (status === 403 || status === 401) {
          invalidateCachedTravelDocumentPresignedUrl(questId, userId, fileName)
          presigned = await resolvePresigned(fileName, true)
          if (cancelled || !presigned) {
            patchFile(index, {
              isLoading: false,
              loadError: true,
              blobUrl: '',
            })
            return
          }
          try {
            const res2 = await Axios.get(presigned, { responseType: 'blob' })
            if (cancelled) return
            applyBlob(presigned, res2.data)
          } catch (e2) {
            patchFile(index, {
              isLoading: false,
              loadError: true,
              blobUrl: '',
            })
            Error.showError(e2)
          }
          return
        }
        patchFile(index, {
          isLoading: false,
          loadError: true,
          blobUrl: '',
        })
        Error.showError(e)
      }
    }

    const prefetchNeighborPresigned = async (neighborIdx) => {
      const files = documentPreviewRef.current.files || []
      if (neighborIdx < 0 || neighborIdx >= files.length) return
      const meta = files[neighborIdx]
      if (!meta?.fileName) return
      const cur = documentPreviewRef.current.files[neighborIdx]
      if (cur?.url && isPresignedDocumentUrlFresh(cur.url)) return
      const p = await resolvePresigned(meta.fileName, true)
      if (cancelled || !p) return
      patchFile(neighborIdx, { url: p })
    }

    const loadActiveDocument = async () => {
      const idx = activeIndexAtStart
      const files = documentPreviewRef.current.files || []
      const file = files[idx]
      if (!file) return

      const slot = files[idx]
      if (
        isTravelDocumentImageFileName(file.fileName) &&
        slot.url &&
        isPresignedDocumentUrlFresh(slot.url)
      ) {
        patchFile(idx, { isLoading: false, loadError: false })
        return
      }
      if (
        isTravelDocumentPdfFileName(file.fileName) &&
        slot.blobUrl &&
        slot.url &&
        isPresignedDocumentUrlFresh(slot.url)
      ) {
        patchFile(idx, { isLoading: false, loadError: false })
        return
      }

      patchFile(idx, { isLoading: true, loadError: false })

      let presigned =
        slot.url && isPresignedDocumentUrlFresh(slot.url)
          ? slot.url
          : await resolvePresigned(file.fileName, true)

      if (cancelled) return
      if (!presigned) {
        patchFile(idx, {
          isLoading: false,
          loadError: true,
          url: '',
          blobUrl: '',
        })
        return
      }

      if (isTravelDocumentImageFileName(file.fileName)) {
        patchFile(idx, {
          url: presigned,
          blobUrl: '',
          isLoading: false,
          loadError: false,
        })
      } else if (isTravelDocumentPdfFileName(file.fileName)) {
        await loadPdfBlobForIndex(idx, file.fileName, presigned)
      } else {
        patchFile(idx, {
          url: presigned,
          blobUrl: '',
          isLoading: false,
          loadError: false,
        })
      }
    }

    ;(async () => {
      await loadActiveDocument()
      if (cancelled) return
      await prefetchNeighborPresigned(activeIndexAtStart - 1)
      if (cancelled) return
      await prefetchNeighborPresigned(activeIndexAtStart + 1)
    })()

    return () => {
      cancelled = true
    }
    // Error.showError is from a stable hook; listing it would retrigger loads unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    documentPreview.show,
    documentPreview.previewSession,
    documentPreview.activeIndex,
    documentPreview.userId,
    questId,
  ])

  const activePreviewFile =
    documentPreview.files?.[documentPreview.activeIndex] || null

  const previewMediaLoadKey =
    activePreviewFile &&
    !activePreviewFile.isLoading &&
    (activePreviewFile.blobUrl || activePreviewFile.url)
      ? [
          documentPreview.userId,
          activePreviewFile.documentType,
          activePreviewFile.fileName,
          activePreviewFile.blobUrl || activePreviewFile.url,
          documentPreview.activeIndex,
        ].join('|')
      : ''

  useLayoutEffect(() => {
    if (!previewMediaLoadKey) {
      setPreviewMediaReady(false)
      return
    }
    setPreviewMediaReady(false)
  }, [previewMediaLoadKey])

  const renderPreviewBody = () => {
    const previewContainerHeight = '100%'

    if (activePreviewFile?.isLoading) {
      return (
        <div
          className='d-flex justify-content-center align-items-center'
          style={{ height: previewContainerHeight }}>
          <FeatherLoader width={48} />
        </div>
      )
    }

    if (activePreviewFile?.loadError) {
      return (
        <div className='h-100 d-flex justify-content-center align-items-center text-muted px-3 text-center'>
          This document could not be loaded. It may have expired; try using
          Previous/Next or close and open the preview again.
        </div>
      )
    }

    if (!activePreviewFile?.url && !activePreviewFile?.blobUrl) {
      return (
        <div className='h-100 d-flex justify-content-center align-items-center text-muted'>
          No preview available.
        </div>
      )
    }

    if (isTravelDocumentImageFileName(activePreviewFile.fileName)) {
      return (
        <div
          className='d-flex align-items-center justify-content-center position-relative'
          style={{ height: previewContainerHeight, overflow: 'hidden' }}>
          {!previewMediaReady && (
            <div className='position-absolute'>
              <FeatherLoader width={48} />
            </div>
          )}
          <img
            src={activePreviewFile.url}
            alt={activePreviewFile.fileName || 'Document Preview'}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: previewMediaReady ? 'block' : 'none',
            }}
            onLoad={() => setPreviewMediaReady(true)}
            onError={() => setPreviewMediaReady(true)}
          />
        </div>
      )
    }

    const iframeSrc = isTravelDocumentPdfFileName(activePreviewFile.fileName)
      ? `${
          activePreviewFile.blobUrl || activePreviewFile.url
        }#toolbar=0&navpanes=0&statusbar=0&messages=0`
      : activePreviewFile.url

    return (
      <div
        className='position-relative'
        style={{ height: previewContainerHeight }}>
        {!previewMediaReady && (
          <div
            className='position-absolute d-flex justify-content-center align-items-center'
            style={{ inset: 0, backgroundColor: '#fff', zIndex: 2 }}>
            <FeatherLoader width={48} />
          </div>
        )}
        <iframe
          src={iframeSrc}
          title={documentPreview.title || 'Document Preview'}
          width='100%'
          height='100%'
          style={{ border: 0, display: previewMediaReady ? 'block' : 'none' }}
          onLoad={() => setPreviewMediaReady(true)}
          onError={() => setPreviewMediaReady(true)}
        />
      </div>
    )
  }

  useEffect(() => {
    if (!documentPreview.show) return undefined

    const handleKeyNavigation = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        setDocumentPreview((prev) => {
          if (!prev.files?.length) return prev
          const nextIndex = Math.max(prev.activeIndex - 1, 0)
          return { ...prev, activeIndex: nextIndex }
        })
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        setDocumentPreview((prev) => {
          if (!prev.files?.length) return prev
          const nextIndex = Math.min(
            prev.activeIndex + 1,
            prev.files.length - 1,
          )
          return { ...prev, activeIndex: nextIndex }
        })
      }
    }

    window.addEventListener('keydown', handleKeyNavigation)
    return () => window.removeEventListener('keydown', handleKeyNavigation)
  }, [documentPreview.show])

  const renderDocumentActions = (participant, documentType) => {
    const userId = participant?.userId?._id
    const loadingKey = getDocumentLoadingKey(userId, documentType)
    const isDownloading = !!docDownloadLoading[loadingKey]
    const isViewing = !!docViewLoading[loadingKey]
    const docLabel = getDocumentLabel(documentType)

    return (
      <span className='experience-doc-acts'>
        <OverlayTrigger
          placement='top'
          overlay={
            <Tooltip id={`view-document-${userId || 'user'}-${documentType}`}>
              View
            </Tooltip>
          }>
          <button
            type='button'
            className='experience-doc-ico'
            aria-label={`View ${docLabel}`}
            disabled={isViewing}
            onClick={() => handleViewTravelDocument(participant, documentType)}>
            {isViewing ? (
              <span
                className='d-inline-flex align-items-center justify-content-center'
                style={{ width: 16, height: 16 }}>
                <FeatherLoader />
              </span>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                aria-hidden='true'>
                <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z' />
                <circle cx='12' cy='12' r='3' />
              </svg>
            )}
          </button>
        </OverlayTrigger>
        <OverlayTrigger
          placement='top'
          overlay={
            <Tooltip
              id={`download-document-${userId || 'user'}-${documentType}`}>
              Download
            </Tooltip>
          }>
          <button
            type='button'
            className='experience-doc-ico'
            aria-label={`Download ${docLabel}`}
            disabled={isDownloading}
            onClick={() =>
              handleDownloadTravelDocument(participant, documentType)
            }>
            {isDownloading ? (
              <span
                className='d-inline-flex align-items-center justify-content-center'
                style={{ width: 16, height: 16 }}>
                <FeatherLoader />
              </span>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                aria-hidden='true'>
                <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                <polyline points='7 10 12 15 17 10' />
                <line x1='12' y1='15' x2='12' y2='3' />
              </svg>
            )}
          </button>
        </OverlayTrigger>
      </span>
    )
  }

  const handleViewEnrollmentFormResponse = (participant) => {
    const answeredAt = participant?.customFormSubmittedAt
    const userId = participant?.userId?._id
    if (!answeredAt || !userId) return

    if (!customFormId) {
      Notification.showNotification(
        'Enrollment form not configured for this experience yet.',
        'danger',
      )
      return
    }

    setSelectedEnrollmentUser({
      userId,
      fullName: participant?.userId?.fullName || '-',
      email: participant?.userId?.email || '',
      submittedAt: answeredAt,
    })
  }

  useLayoutEffect(() => {
    if (typeof onHeaderActionsChange !== 'function') return undefined

    onHeaderActionsChange(
      <>
        {!questDetail?.isArchived && (
          <button
            type='button'
            className='btn-outline-experience'
            onClick={handleAddNewQuestClick}
            disabled={!questDetail?.isPublished}>
            Add participant
          </button>
        )}
        <button
          type='button'
          className='btn-outline-experience'
          id='generate-report'
          disabled={
            exportLoading ||
            downloadDocsLoading ||
            !customFormId ||
            !hasAnyEnrollmentFormSubmitted
          }
          onClick={handleExportReport}>
          {exportLoading ? 'Exporting...' : 'Export form answers'}
        </button>
        <button
          type='button'
          className='btn-outline-experience'
          id='download-travel-documents'
          disabled={
            exportLoading || downloadDocsLoading || !hasAnyTravelDocuments
          }
          onClick={handleDownloadTravelDocuments}>
          {downloadDocsLoading ? 'Downloading...' : 'Download travel documents'}
        </button>
      </>,
    )

    return () => onHeaderActionsChange(null)
  }, [
    onHeaderActionsChange,
    questDetail?.isArchived,
    questDetail?.isPublished,
    exportLoading,
    downloadDocsLoading,
    customFormId,
    hasAnyEnrollmentFormSubmitted,
    hasAnyTravelDocuments,
  ])

  return (
    <>
      <Helmet>
        <title>{`Experience-Participants | Abroad`}</title>
        <meta name='title' content={'Experience-Participants | Abroad'}></meta>
        <meta
          property='og:title'
          content={'Experience-Participants | Abroad'}></meta>
        <meta
          property='twitter:title'
          content={'Experience-Participants | Abroad'}></meta>
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/admin/experiences/${questId}/participants`}></link>
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}/admin/experiences/${questId}/participants`}></meta>
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}/admin/experiences/${questId}/participants`}></meta>
      </Helmet>
      <div className='experience-manage-list'>
        <div className='experience-sumrow'>
          <div className='experience-stat'>
            <span>On the roster</span>
            <b>{participantStats.rosterCount}</b>
            <small>
              {rosterCapacity != null
                ? `of ${rosterCapacity} people`
                : 'participants'}
            </small>
          </div>
          <div className='experience-stat'>
            <span>Outstanding</span>
            <b>${Number(participantStats.outstandingTotal).toLocaleString()}</b>
            <small>
              {participantStats.outstandingPeople}{' '}
              {participantStats.outstandingPeople === 1 ? 'person' : 'people'}
            </small>
          </div>
          <div className='experience-stat'>
            <span>Documents missing</span>
            <b>{participantStats.docsMissing}</b>
            <small>
              across {participantStats.docsMissingPeople}{' '}
              {participantStats.docsMissingPeople === 1 ? 'person' : 'people'}
            </small>
          </div>
        </div>
        <div className='experience-tbl-wrap'>
          <table className='experience-tbl experience-roster-tbl'>
            <thead>
              <tr>
                <SortableHeaderCell
                  label='Name'
                  isSorted={participantSortBy === PARTICIPANT_SORT_BY.NAME}
                  isSortedDesc={!participantSortAsc}
                  onSort={handleParticipantSortByName}
                />
                <th>Payment</th>
                <th>Enrollment Form</th>
                <th>Air Ticket</th>
                <th>Insurance</th>
                <th>Passport</th>
                <th>Headshot</th>
                <SortableHeaderCell
                  label='Signup Date'
                  className='text-center'
                  wrapperClassName='justify-content-center'
                  isSorted={participantSortBy === PARTICIPANT_SORT_BY.DATE}
                  isSortedDesc={!participantSortAsc}
                  onSort={handleParticipantSortByDate}
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td className='text-center border-0 pt-3' colSpan='9'>
                    <FeatherLoader />
                  </td>
                </tr>
              )}
              {questParticipants?.length === 0 && !isLoading && (
                <tr>
                  <td className='text-center s6 py-3' colSpan='9'>
                    No participants for this experience yet. Please use add new
                    user or go to user profile and add user to this experience.{' '}
                  </td>
                </tr>
              )}
              {sortedQuestParticipants?.length > 0 &&
                !isLoading &&
                sortedQuestParticipants.map((participant, index) => {
                  const paymentProgress =
                    getParticipantPaymentProgress(participant)
                  return (
                    <tr
                      key={index}
                      id={participant?.userId?._id}
                      className={classNames({
                        'highlight-row':
                          participant?.userId?._id &&
                          `#${String(participant.userId._id)}` ===
                            (location?.hash || '').trim() &&
                          showHighlightedPart,
                      })}>
                      <td className='experience-who'>
                        <b>
                          <Link
                            to={`/admin/users/${participant?.userId?._id}`}
                            className='experience-who-name'
                            rel='noopener noreferrer'
                            target='_blank'>
                            {participant?.userId?.fullName}
                          </Link>
                        </b>
                        <span>
                          <Link
                            to={`/admin/users/${participant?.userId?._id}`}
                            className='experience-who-email'
                            rel='noopener noreferrer'
                            target='_blank'>
                            {participant?.userId?.email}
                          </Link>
                        </span>
                      </td>
                      <td className='experience-pay-cell'>
                        <b>{paymentProgress.label}</b>
                        <div className='experience-pay-track'>
                          <i
                            className={
                              paymentProgress.isPartial ? 'part' : undefined
                            }
                            style={{ width: `${paymentProgress.pct}%` }}
                          />
                        </div>
                      </td>
                      <td>
                        {!participant?.customFormSubmittedAt ? (
                          '—'
                        ) : (
                          <button
                            type='button'
                            className='experience-linkbtn'
                            onClick={() =>
                              handleViewEnrollmentFormResponse(participant)
                            }>
                            View
                          </button>
                        )}
                      </td>
                      <td>
                        {getTravelDocumentFileName(participant, 'ticket')
                          ? renderDocumentActions(participant, 'ticket')
                          : '—'}
                      </td>
                      <td>
                        {getTravelDocumentFileName(participant, 'insurance')
                          ? renderDocumentActions(participant, 'insurance')
                          : '—'}
                      </td>
                      <td>
                        {getTravelDocumentFileName(participant, 'passport')
                          ? renderDocumentActions(participant, 'passport')
                          : '—'}
                      </td>
                      <td>
                        {getTravelDocumentFileName(participant, 'headshot')
                          ? renderDocumentActions(participant, 'headshot')
                          : '—'}
                      </td>
                      <td>{UTCFormatDate(participant?.createdAt)}</td>
                      <td>
                        <Dropdown>
                          <Dropdown.Toggle as={CustomToggle} />
                          <Dropdown.Menu
                            align='end'
                            className='quest-participant-menu'
                            popperConfig={{
                              strategy: 'fixed',
                              modifiers: [
                                {
                                  name: 'preventOverflow',
                                  enabled: false,
                                },
                                { name: 'hide', enabled: false },
                              ],
                            }}
                            style={{ zIndex: 9999 }}>
                            <Dropdown.Item
                              className='text-dark'
                              disabled={isAnyInviteActionLoading(
                                participant?.userId?._id,
                              )}
                              onClick={() =>
                                handleResendInvitationEmail(participant)
                              }>
                              {isInviteActionLoading(
                                participant?.userId?._id,
                                'resend',
                              ) ? (
                                <span className='d-inline-flex align-items-center'>
                                  <Spinner
                                    as='span'
                                    animation='border'
                                    size='sm'
                                    role='status'
                                    className='text-saffron-700 mr-2'
                                  />
                                  Sending Invitation
                                </span>
                              ) : (
                                'Resend Invitation Email'
                              )}
                            </Dropdown.Item>
                            <Dropdown.Item
                              className='text-dark'
                              disabled={isAnyInviteActionLoading(
                                participant?.userId?._id,
                              )}
                              onClick={() =>
                                handleCopyInvitationLink(participant)
                              }>
                              {isInviteActionLoading(
                                participant?.userId?._id,
                                'copy',
                              ) ? (
                                <span className='d-inline-flex align-items-center'>
                                  <Spinner
                                    as='span'
                                    animation='border'
                                    size='sm'
                                    role='status'
                                    className='text-saffron-700 mr-2'
                                  />
                                  Generating Link
                                </span>
                              ) : (
                                'Copy Invitation Link'
                              )}
                            </Dropdown.Item>
                            <Dropdown.Item
                              className='text-dark'
                              onClick={() => handleManualPayment(participant)}>
                              Manual Payment
                            </Dropdown.Item>
                            <Dropdown.Item
                              className='text-dark'
                              onClick={() => handleUpdateClick(participant)}>
                              Update Payment
                            </Dropdown.Item>
                            <Dropdown.Item
                              className='text-danger'
                              onClick={() =>
                                setParticipantToRemove(participant)
                              }>
                              Remove
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
        {selectedEnrollmentUser ? (
          <QuestTypeformUserResponse
            userId={selectedEnrollmentUser.userId}
            userName={selectedEnrollmentUser.fullName}
            userEmail={selectedEnrollmentUser.email}
            submittedAt={selectedEnrollmentUser.submittedAt}
            customFormId={customFormId}
            questId={questId}
            onBack={() => setSelectedEnrollmentUser(null)}
          />
        ) : null}
        <AddUserModal
          showModal={showModal}
          handleClose={handleCloseModal}
          updateParticipants={refreshParticipants}
          questParticipants={questParticipants}
        />
        <UserDetailsModal
          showModal={showUserDetailsModal}
          handleClose={handleCloseUserDetailsModal}
          userData={selectedUser}
          participantData={selectedParticipant}
          updateParticipants={refreshParticipants}
          investments={investments}
          questDetails={questDetail}
        />
        <ExperienceConfirmModal
          show={!!participantToRemove}
          onHide={() => setParticipantToRemove(null)}
          onConfirm={() => deleteQuestUser(participantToRemove?.userId?._id)}
          title='Remove this participant?'
          message={`${
            participantToRemove?.userId?.fullName ||
            participantToRemove?.userId?.email ||
            'This participant'
          } will be removed from this experience.${
            Number(participantToRemove?.totalPaid) > 0
              ? ` They have already paid $${Number(
                  participantToRemove.totalPaid,
                ).toLocaleString()}.`
              : ''
          }`}
          confirmLabel='Remove'
          loadingLabel='Removing…'
          isLoading={isRemoving}
        />
        <ManualPaymentModal
          showModal={showManualPaymentModal}
          handleClose={handleCloseManualPaymentModal}
          participant={selectedParticipant}
          updateParticipants={refreshParticipants}
        />
        <Modal
          show={documentPreview.show}
          onHide={closeDocumentPreview}
          centered
          dialogClassName='experience-doc-dialog'
          contentClassName='experience-doc-card'>
          <Modal.Body className='p-0'>
            <div className='experience-doc-modal experience-doc-modal-lg'>
              <h2>
                {activePreviewFile?.label ||
                  documentPreview.title ||
                  'Document'}
              </h2>
              {documentPreview.userName ? (
                <p className='experience-doc-lead'>
                  {documentPreview.userName}
                </p>
              ) : null}
              <div className='experience-doc-preview'>
                <div
                  className={classNames('experience-doc-preview-frame', {
                    'is-headshot': /headshot/i.test(
                      activePreviewFile?.label || documentPreview.title || '',
                    ),
                    'is-loading':
                      activePreviewFile?.isLoading || !previewMediaReady,
                  })}>
                  {renderPreviewBody()}
                </div>
                {activePreviewFile?.fileName ? (
                  <small>{activePreviewFile.fileName}</small>
                ) : null}
              </div>
              {documentPreview.files?.length > 1 ? (
                <div className='experience-doc-pager'>
                  <button
                    type='button'
                    className='btn-outline-experience'
                    disabled={documentPreview.activeIndex === 0}
                    onClick={goToPreviousDocument}>
                    Previous
                  </button>
                  <span className='experience-doc-pager-count'>
                    {documentPreview.activeIndex + 1} /{' '}
                    {documentPreview.files.length}
                  </span>
                  <button
                    type='button'
                    className='btn-outline-experience'
                    disabled={
                      documentPreview.activeIndex ===
                      documentPreview.files.length - 1
                    }
                    onClick={goToNextDocument}>
                    Next
                  </button>
                </div>
              ) : null}
              <div className='experience-modal-actions'>
                {activePreviewFile?.url || activePreviewFile?.blobUrl ? (
                  <button
                    type='button'
                    className='btn-outline-experience'
                    onClick={() => {
                      const href =
                        activePreviewFile.blobUrl || activePreviewFile.url
                      const link = document.createElement('a')
                      link.href = href
                      link.download = activePreviewFile.fileName || 'document'
                      link.target = '_blank'
                      link.rel = 'noopener noreferrer'
                      document.body.appendChild(link)
                      link.click()
                      document.body.removeChild(link)
                    }}>
                    Download
                  </button>
                ) : null}
                <button
                  type='button'
                  className='btn-outline-experience'
                  onClick={closeDocumentPreview}>
                  Close
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  )
}

export default QuestsParticipants
